import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';

/* Página pública de resposta a uma consulta (acedida por link, sem login). */
const ConsultaPublica = () => {
    const { token } = useParams();
    const [consulta, setConsulta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [respostas, setRespostas] = useState({});
    const [nome, setNome] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submetido, setSubmetido] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get(`/consultas/public/${token}`);
                setConsulta(res.data);
            } catch (err) {
                setErro(err.response?.data?.error || 'Consulta não disponível.');
            } finally { setLoading(false); }
        })();
    }, [token]);

    const setSingle = (pid, val) => setRespostas(p => ({ ...p, [pid]: val }));
    const setText = (pid, val) => setRespostas(p => ({ ...p, [pid]: val }));
    const toggleMulti = (pid, opt) => setRespostas(p => {
        const arr = Array.isArray(p[pid]) ? p[pid] : [];
        return { ...p, [pid]: arr.includes(opt) ? arr.filter(o => o !== opt) : [...arr, opt] };
    });

    const submeter = async () => {
        // Validar obrigatórias
        for (const pg of consulta.perguntas) {
            if (!pg.obrigatoria) continue;
            const v = respostas[pg.id];
            const vazio = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
            if (vazio) { setErro('Por favor responda a todas as perguntas obrigatórias (assinaladas com *).'); window.scrollTo(0, 0); return; }
        }
        setSubmitting(true); setErro(null);
        try {
            await api.post(`/consultas/public/${token}/responder`, { respostas, respondente_nome: nome || null });
            setSubmetido(true);
            window.scrollTo(0, 0);
        } catch (err) {
            setErro(err.response?.data?.error || 'Erro ao submeter. Tente novamente.');
        } finally { setSubmitting(false); }
    };

    if (loading) return <div style={wrap}><p style={{ color: '#64748b' }}>A carregar…</p></div>;

    if (erro && !consulta) return (
        <div style={wrap}>
            <div style={card}>
                <div style={{ fontSize: 40, textAlign: 'center' }}>🔒</div>
                <p style={{ textAlign: 'center', color: '#475569' }}>{erro}</p>
            </div>
        </div>
    );

    if (submetido) return (
        <div style={wrap}>
            <div style={card}>
                <div style={{ fontSize: 48, textAlign: 'center' }}>✅</div>
                <h2 style={{ textAlign: 'center', color: '#166534', margin: '8px 0' }}>Resposta enviada!</h2>
                <p style={{ textAlign: 'center', color: '#475569' }}>Obrigado pela sua participação.</p>
            </div>
        </div>
    );

    return (
        <div style={wrap}>
            <div style={card}>
                <h1 style={{ fontSize: '1.35rem', color: '#1e293b', margin: '0 0 6px' }}>{consulta.titulo}</h1>
                {consulta.descricao && <p style={{ color: '#64748b', marginTop: 0 }}>{consulta.descricao}</p>}
                <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 12 }}>
                    {consulta.anonima ? 'Resposta anónima.' : 'Identifique-se abaixo.'} Campos com * são obrigatórios.
                </div>

                {erro && <div style={errBox}>{erro}</div>}

                {!consulta.anonima && (
                    <div style={qBlock}>
                        <label style={qLabel}>O seu nome</label>
                        <input value={nome} onChange={e => setNome(e.target.value)} style={inp} placeholder="Nome" />
                    </div>
                )}

                {consulta.perguntas.map((pg, idx) => (
                    <div key={pg.id} style={qBlock}>
                        {pg.tema && <div style={temaTag}>{pg.tema}</div>}
                        <label style={qLabel}>
                            <span style={{ color: '#3182ce', fontWeight: 700 }}>{idx + 1}.</span> {pg.texto}
                            {pg.obrigatoria && <span style={{ color: '#e53e3e' }}> *</span>}
                        </label>

                        {pg.tipo === 'text' && (
                            <textarea value={respostas[pg.id] || ''} onChange={e => setText(pg.id, e.target.value)}
                                rows={3} style={{ ...inp, resize: 'vertical' }} />
                        )}

                        {pg.tipo === 'single' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                {pg.opcoes.map(opt => {
                                    const sel = respostas[pg.id] === opt;
                                    return (
                                        <button key={opt} type="button" onClick={() => setSingle(pg.id, opt)} style={optBtn(sel)}>
                                            <span style={radio(sel)} />{opt}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {pg.tipo === 'multiple' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                {pg.opcoes.map(opt => {
                                    const arr = Array.isArray(respostas[pg.id]) ? respostas[pg.id] : [];
                                    const sel = arr.includes(opt);
                                    return (
                                        <button key={opt} type="button" onClick={() => toggleMulti(pg.id, opt)} style={optBtn(sel)}>
                                            <span style={check(sel)}>{sel ? '✓' : ''}</span>{opt}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}

                <button onClick={submeter} disabled={submitting} style={submitBtn(submitting)}>
                    {submitting ? 'A enviar…' : 'Enviar resposta'}
                </button>
            </div>
        </div>
    );
};

const wrap = { minHeight: '100vh', background: '#f1f5f9', padding: '24px 14px', display: 'flex', justifyContent: 'center' };
const card = { background: '#fff', borderRadius: 14, padding: '24px 22px', width: '100%', maxWidth: 640, boxShadow: '0 4px 24px rgba(0,0,0,0.08)' };
const qBlock = { padding: '14px 0', borderTop: '1px solid #f1f5f9' };
const qLabel = { display: 'block', fontWeight: 600, color: '#334155', fontSize: '0.95rem', lineHeight: 1.4 };
const temaTag = { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.95rem', marginTop: 6, boxSizing: 'border-box' };
const errBox = { background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 12, fontSize: '0.88rem' };
const optBtn = (sel) => ({
    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
    padding: '11px 14px', borderRadius: 10, cursor: 'pointer', fontSize: '0.95rem',
    border: sel ? '1.5px solid #3182ce' : '1px solid #e2e8f0',
    background: sel ? '#ebf5ff' : '#fff', color: sel ? '#1e40af' : '#334155', fontWeight: sel ? 600 : 400,
});
const radio = (sel) => ({
    width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
    border: sel ? '5px solid #3182ce' : '2px solid #cbd5e1', boxSizing: 'border-box',
});
const check = (sel) => ({
    width: 18, height: 18, borderRadius: 5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, color: '#fff', border: sel ? 'none' : '2px solid #cbd5e1', background: sel ? '#3182ce' : '#fff',
});
const submitBtn = (busy) => ({
    marginTop: 22, width: '100%', padding: '13px', borderRadius: 10, border: 'none',
    background: busy ? '#94a3b8' : '#3182ce', color: '#fff', fontSize: '1rem', fontWeight: 600, cursor: busy ? 'default' : 'pointer',
});

export default ConsultaPublica;
